import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Link } from 'react-router-dom';
import { LoanApplication } from '../../types';
import axios from 'axios';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import ApplicationStatus from '../ApplicationStatus';

const backendUrl = 'http://localhost:3001/api';

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
    <strong>{label}</strong>
    <span>{value}</span>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <h3 style={{ marginTop: 0 }}>{title}</h3>
    {children}
  </div>
);

const MerchantDashboard: React.FC = () => {
  const merchantId = useSelector((s: RootState) => s.auth.user?.merchantId);
  const [apps, setApps] = useState<LoanApplication[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [series, setSeries] = useState<{ loanActivities: any[]; loans: any[] }>({ loanActivities: [], loans: [] });

  useEffect(() => {
    if (!merchantId) return;
    axios.get(`${backendUrl}/applications`, { params: { merchantId } }).then(r => setApps(r.data));
    axios.get(`${backendUrl}/metrics/summary`, { params: { merchantId } }).then(r => setSummary(r.data));
    axios.get(`${backendUrl}/metrics/timeseries`, { params: { merchantId } }).then(r => setSeries(r.data));
  }, [merchantId]);

  return (
    <div style={{ padding: 16 }}>
      <h2>Merchant Portal</h2>
      <p>Welcome. Track your loan applications and account metrics.</p>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 20, 
        marginBottom: 24,
        alignItems: 'stretch'
      }}>
        <KpiCard 
          title="Total Disbursed" 
          value={summary ? `${Number(summary.totalDisbursed || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#1565c0" 
          deltaPct={summary?.deltas?.disbursed} 
          subtitle="vs prev period"
          clickable={true}
          details={{
            type: 'disbursements',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.disbursementAmount || 0
            })) || [],
            summary: {
              totalDisbursed: summary?.totalDisbursed,
              avgDisbursed: summary?.totalDisbursed && summary?.totalLoans ? summary.totalDisbursed / summary.totalLoans : 0,
              totalLoans: summary?.totalLoans
            }
          }}
        />
        <KpiCard 
          title="Total Repaid" 
          value={summary ? `${Number(summary.totalRepaid || 0).toLocaleString('en-ZW')} ZiG` : '—'} 
          color="#12b569"
          clickable={true}
          details={{
            type: 'repayments',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.repaymentAmount || 0
            })) || [],
            summary: {
              totalRepaid: summary?.totalRepaid,
              repaymentRate: summary?.repaymentRate,
              onTimePayments: summary?.onTimePayments
            }
          }}
        />
        <KpiCard 
          title="Loans Submitted" 
          value={summary ? Number(summary.totalApplications || 0) : '—'} 
          color="#1976d2" 
          deltaPct={summary?.deltas?.applications} 
          subtitle="vs prev period"
          clickable={true}
          details={{
            type: 'applications',
            data: apps.slice(0, 10).map((app: any) => ({
              applicantName: app.applicantName,
              submissionDate: app.submissionDate,
              requestedAmount: app.requestedAmount
            })),
            summary: {
              totalApplications: summary?.totalApplications,
              pendingApplications: summary?.pendingApplications,
              avgApplicationAmount: summary?.avgApplicationAmount
            }
          }}
        />
        <KpiCard 
          title="Approval Rate" 
          value={summary && summary.totalLoans ? `${Math.round((summary.approvedLoans / summary.totalLoans) * 100)}%` : '—'} 
          color="#00bcd4"
          clickable={true}
          details={{
            type: 'approved',
            data: apps.filter((app: any) => app.status === 'approved').slice(0, 10).map((app: any) => ({
              applicantName: app.applicantName,
              approvalDate: app.submissionDate,
              requestedAmount: app.requestedAmount
            })),
            summary: {
              approvedLoans: summary?.approvedLoans,
              totalApprovedAmount: summary?.totalApprovedAmount,
              approvalRate: summary?.approvedLoans && summary?.totalLoans ? summary.approvedLoans / summary.totalLoans : 0
            }
          }}
        />
        <KpiCard 
          title="Repayment Rate" 
          value={summary ? `${Math.round((summary.repaymentRate || 0) * 100)}%` : '—'} 
          color="#ff8c00"
          clickable={true}
          details={{
            type: 'repayments',
            data: series.loanActivities?.map((v: any) => ({
              applicantName: `Daily Activity`,
              date: v.date,
              amount: v.repaymentAmount || 0
            })) || [],
            summary: {
              totalRepaid: summary?.totalRepaid,
              repaymentRate: summary?.repaymentRate,
              onTimePayments: summary?.onTimePayments
            }
          }}
        />
      </div>

      <Card title="Recent Loan Activities">
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={series.loanActivities || []} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorDisbM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1565c0" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#1565c0" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRepayM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#12b569" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#12b569" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="disbursementAmount" stroke="#1565c0" fillOpacity={1} fill="url(#colorDisbM)" name="Disbursements" />
              <Area type="monotone" dataKey="repaymentAmount" stroke="#12b569" fillOpacity={1} fill="url(#colorRepayM)" name="Repayments" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Your Loan Applications">
        {apps.length === 0 && <div>No applications yet.</div>}
        {apps.map(a => (
          <div key={a.id} style={{ padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div><strong>{a.applicantName}</strong> — {a.requestedAmount.toLocaleString('en-ZW')} ZiG</div>
                <div style={{ fontSize: 12, color: '#666' }}>Submitted {new Date(a.submissionDate).toLocaleDateString()}</div>
              </div>
              <div style={{ textTransform: 'capitalize' }}>{a.status}</div>
            </div>
            <div style={{ marginTop: 8 }}>
              <Link to={`/application/${a.id}`}>View lifecycle</Link>
            </div>
          </div>
        ))}
      </Card>

      {/* Application Status Component */}
      <ApplicationStatus />

    </div>
  );
};

export default MerchantDashboard;

